import { wencesApi } from "@/api/wencesApi";
import { isAxiosError } from "axios";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 400;

/** Se incrementa al inicio de cada navegación (router.beforeEach global). */
let navGeneration = 0;
/** Resultado reutilizable dentro de la misma navegación: { gen, result }. */
let navCache = null;
/** Petición en curso para esta generación: { gen, promise }. */
let navInflight = null;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Llamar una vez al comienzo de cada transición de ruta (p. ej. primer hook global). */
export function beginCheckAuthNavigation() {
  navGeneration += 1;
  navCache = null;
}

/**
 * Tras completar la navegación: invalida solo el resultado cacheado para esa transición.
 * No toca `navInflight`: puede haber un check-status aún en curso (p. ej. `Login` en `created`).
 */
export function clearCheckAuthNavigationCache() {
  navCache = null;
}

/** Errores típicos de servidor caído, reinicio o proxy — no invalidan el token en cliente */
function isTransientAuthError(error) {
  if (!isAxiosError(error)) return true;
  if (!error.response) return true;
  const s = error.response.status;
  return s === 500 || s === 502 || s === 503 || s === 504;
}

function transientSuccessResponse(localToken) {
  return {
    ok: true,
    transient: true,
    user: undefined,
    token: localToken,
    needsToAcceptTerms: false,
    termsVersion: null,
  };
}

async function fetchCheckStatus(localToken) {
  const { data } = await wencesApi.get("/api/users/check-status", {
    headers: {
      Authorization: `Bearer ${localToken}`,
    },
  });

  const responseData = data.data || data;

  return {
    ok: true,
    transient: false,
    user: responseData.user,
    token: responseData.token || localToken,
    needsToAcceptTerms: responseData.needsToAcceptTerms || false,
    termsVersion: responseData.termsVersion || null,
  };
}

async function runCheckAuthFetch(localToken) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await fetchCheckStatus(localToken);
    } catch (error) {
      lastError = error;
      if (isAxiosError(error) && error.response?.status === 401) {
        return { ok: false };
      }
      if (isTransientAuthError(error) && attempt < MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS);
        continue;
      }
      if (isTransientAuthError(error)) {
        return transientSuccessResponse(localToken);
      }
      throw new Error("No se pudo verificar la sesión");
    }
  }
  throw lastError;
}

/**
 * Verifica sesión vía GET /api/users/check-status.
 * Dentro de la misma navegación (misma generación tras `beginCheckAuthNavigation`) reutiliza
 * una sola petición HTTP y el resultado en caché.
 *
 * @param {{ force?: boolean }} [options] — `force: true` omite caché/dedupe (login explícito, etc.).
 */
export const checkAuthAction = async (options = {}) => {
  const force = options.force === true;
  const localToken = localStorage.getItem("token");
  if (localToken && localToken.length < 10) {
    return { ok: false };
  }

  if (!localToken) {
    return { ok: false };
  }

  if (force) {
    return runCheckAuthFetch(localToken);
  }

  const gen = navGeneration;
  if (navCache && navCache.gen === gen) {
    return navCache.result;
  }
  if (navInflight && navInflight.gen === gen) {
    return navInflight.promise;
  }

  const promise = runCheckAuthFetch(localToken)
    .then((result) => {
      if (navGeneration === gen) {
        navCache = { gen, result };
      }
      return result;
    })
    .finally(() => {
      if (navInflight && navInflight.gen === gen) {
        navInflight = null;
      }
    });

  navInflight = { gen, promise };
  return promise;
};
